import * as React from "react";

import { FormField } from "Components/ui";
import { addDaysToDate, formatDate, isDateOnly, todayIST } from "utils/format";
import ConfirmDialog from "Routes/Billing/Components/ConfirmDialog";
import { DateInput } from "Routes/Billing/Components/ui";

const MAX_PAUSE_DAYS = 365;

interface PauseEnforcementDialogProps {
  onConfirm: (pausedUntil: string, reason: string) => Promise<unknown>;
  onClose: () => void;
}

/** Pause warnings / read-only through a date (last paused day inclusive, ≤ 365 days). */
const PauseEnforcementDialog: React.FC<PauseEnforcementDialogProps> = ({ onConfirm, onClose }) => {
  const today = todayIST();
  const maxDate = addDaysToDate(today, MAX_PAUSE_DAYS);
  const [pausedUntil, setPausedUntil] = React.useState<string>(addDaysToDate(today, 7));
  const blockingError = !isDateOnly(pausedUntil)
    ? "Pick the last paused day."
    : pausedUntil < today
    ? "Pick today or a later date."
    : pausedUntil > maxDate
    ? `Pause for at most ${MAX_PAUSE_DAYS} days.`
    : null;

  return (
    <ConfirmDialog
      title="Pause enforcement"
      description={
        <>
          TeleOpd shows no warning or read-only notice until 10:00 IST on{" "}
          <strong className="font-semibold text-foreground">
            {isDateOnly(pausedUntil) ? formatDate(addDaysToDate(pausedUntil, 1)) : "—"}
          </strong>
          . Invoices, reminders and payments carry on as usual.
        </>
      }
      confirmLabel="Pause enforcement"
      requireReason
      blockingError={blockingError}
      onConfirm={(reason) => onConfirm(pausedUntil, reason)}
      onClose={onClose}
    >
      <FormField
        label="Pause through (last paused day)"
        id="pause-until"
        required
        hint={`Up to ${formatDate(maxDate)}.`}
      >
        <DateInput
          id="pause-until"
          value={pausedUntil}
          min={today}
          max={maxDate}
          onChange={setPausedUntil}
        />
      </FormField>
    </ConfirmDialog>
  );
};

export default PauseEnforcementDialog;
